/**
 * Unified map search: places, tenements, soil samples, drill holes, A-Numbers.
 *
 * The search box used to be MapTiler's geocoder alone, so it could find a town
 * and nothing else - every dataset lookup lived in a tab inside a filter panel,
 * which is not where anyone looks when they have an ID in their hand.
 *
 * Everything here returns the same normalised result shape so the modal and the
 * fly-to logic never have to care which source a row came from:
 *
 *   { id, source, title, subtitle, meta, lngLat, bbox?, feature? }
 *
 * Queries are dispatched by pattern (see `classify`), not run all at once: a
 * tenement ID cannot be a place name, and firing five WFS requests per keystroke
 * on a viewport-limited GeoServer is how you take the map down.
 *
 * ## What is and is not indexed (measured against prod, 2026-08-26)
 *
 *   tenements.fmt_tenid, .holdername      0.6s    fine unbounded
 *   *.anumber                             0.2s    fine unbounded
 *   drill/soil geometry (BBOX)            0.3s    fine
 *   soil.companysampleid                  6-46s   SEQ SCAN - no index
 *   drill.companyholeid                   >60s    SEQ SCAN - no index, 502s
 *
 * Sample and hole IDs are searched across the whole state (WA_BBOX), not the
 * current view: you search for an ID precisely when you do NOT know where it
 * is, so a viewport-limited answer is the wrong answer.
 *
 * That used to be unaffordable - 6-46s on soils, >60s and a load-balancer 502
 * on drill holes. Indexed 2026-08-30 (infrastructure/sql/2026-08-29-index-tuning.sql)
 * and both are now ~0.2s.
 *
 * The index that matters is NOT on the raw column. GeoServer renders a
 * case-insensitive CQL LIKE as `upper(col) LIKE upper(pattern)`, so a trigram
 * index on `companysampleid` is never reachable from the map - it has to be on
 * `upper(companysampleid::text)`. A plain btree is no use to either form,
 * because a leading wildcard cannot use one at all.
 */

import axios from 'axios';

const GoldFinderGeoserver = axios.create({
  baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER,
});

const WA_BBOX = [112.565918, -35.353216, 128.957520, -13.154376];

/**
 * Held the two company-ID sources back while their columns were unindexed.
 * The indexes are in as of 2026-08-30 and both queries measure ~0.2s through
 * GeoServer, so this is off. Kept as a switch rather than deleted: if the
 * refresh pipeline ever drops those indexes again - it has form, see the note
 * in the DDL script - flipping this back is the one-line mitigation.
 */
export const SLOW_WITHOUT_INDEX = false;

/**
 * Shared WFS GET. `cql` is already-escaped ECQL. Pass `bbox` to bound a query
 * that would otherwise sequentially scan - see the note above.
 */
const wfs = (typeName, cql, { limit = 200, signal, propertyName, bbox } = {}) =>
  GoldFinderGeoserver.get('/ne/ows?', {
    signal,
    params: {
      service: 'WFS',
      version: '1.1.0',
      request: 'GetFeature',
      typeNames: typeName,
      maxFeatures: limit,
      outputFormat: 'application/json',
      srs: 'EPSG:4326',
      cql_filter: bbox ? `BBOX(geom,${bbox.join(',')}) AND (${cql})` : cql,
      ...(propertyName ? { propertyName } : {}),
    },
  });

/** ECQL string literal escape - a lone apostrophe in a holder name is enough. */
const q = (s) => `'${String(s).replace(/'/g, "''")}'`;

/**
 * Centroid of any geometry, good enough to fly to. Tenements are polygons and
 * samples are points, so this avoids pulling turf in for one operation.
 */
const centreOf = (geometry) => {
  if (!geometry) return null;
  if (geometry.type === 'Point') return { lng: geometry.coordinates[0], lat: geometry.coordinates[1] };
  const coords = [];
  const walk = (c) => (typeof c[0] === 'number' ? coords.push(c) : c.forEach(walk));
  walk(geometry.coordinates);
  if (!coords.length) return null;
  const lng = coords.reduce((a, c) => a + c[0], 0) / coords.length;
  const lat = coords.reduce((a, c) => a + c[1], 0) / coords.length;
  return { lng, lat };
};

const bboxOf = (geometry) => {
  if (!geometry || geometry.type === 'Point') return null;
  const coords = [];
  const walk = (c) => (typeof c[0] === 'number' ? coords.push(c) : c.forEach(walk));
  walk(geometry.coordinates);
  if (!coords.length) return null;
  const lngs = coords.map((c) => c[0]);
  const lats = coords.map((c) => c[1]);
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
};

/**
 * Tenement IDs do not all look like "P 51/1234". Measured against prod
 * (2026-09-12, all 452,736 rows of wa_geodata.tenements):
 *
 *   P 51/1234      440,888   plain
 *   E 04/1131-I     10,134   hyphen suffix: I, S, G, MEP, EOS, MEL, MPL, B, N
 *   AC 01/219A       1,567   bare-letter suffix, no hyphen
 *   G 8SA               72   State Agreement, no "/" at all
 *   WA-43-MEL           70   petroleum/offshore title
 *
 * Only the first form used to parse, so the other 11,843 - which are mostly
 * dead ground, and are exactly the IDs people paste off an old report - fell
 * through to the geocoder and came back as place names.
 *
 * Returns { base, suffix, sep, exact } or null.
 *   base    the ID without its suffix ("E 04/1131")
 *   suffix  the suffix as typed, uppercased, or null
 *   sep     "-" if the suffix was typed hyphenated, "" if bare
 *   exact   true for shapes that must match whole, never by prefix
 */
const TEN_ID = /^([A-Za-z]{1,3})\s*(\d+)\s*\/\s*(\d+)\s*(?:-\s*([A-Za-z0-9]{1,4})|([A-Za-z]{1,3}))?$/;
const TEN_ID_STATE_AGREEMENT = /^([A-Za-z]{1,3})\s*(\d+)\s*SA$/i;
const TEN_ID_PETROLEUM = /^WA\s*-\s*(\d+)\s*-\s*([A-Za-z]{2,4})$/i;

export const parseTenId = (input) => {
  const raw = String(input).trim();

  const pet = raw.match(TEN_ID_PETROLEUM);
  if (pet) return { base: `WA-${pet[1]}-${pet[2].toUpperCase()}`, suffix: null, sep: '', exact: true };

  const sa = raw.match(TEN_ID_STATE_AGREEMENT);
  if (sa) return { base: `${sa[1].toUpperCase()} ${sa[2]}SA`, suffix: null, sep: '', exact: true };

  const m = raw.match(TEN_ID);
  if (!m) return null;
  return {
    base: `${m[1].toUpperCase()} ${m[2]}/${m[3]}`,
    suffix: (m[4] || m[5] || '').toUpperCase() || null,
    sep: m[4] ? '-' : '',
    exact: false,
  };
};

/**
 * Normalises the many ways a tenement ID gets typed - "p51/1234", "P 51/1234",
 * "P51 / 1234" - into the "P 51/1234" form GeoServer stores in fmt_tenid.
 */
export const normaliseTenId = (input) => {
  const p = parseTenId(input);
  if (!p) return null;
  return p.suffix ? `${p.base}${p.sep}${p.suffix}` : p.base;
};

/**
 * Does a stored fmt_tenid answer what was typed?
 *
 * Needed because a base ID is matched by prefix - "E 04/1131" has to reach
 * "E 04/1131-I" - and a bare prefix LIKE would also drag in "E 04/11310".
 * The suffix is therefore allowed to be nothing, "-" + up to 4 alphanumerics,
 * or up to 3 bare letters, and nothing else.
 */
const tenIdMatches = (stored, { base, suffix, exact }) => {
  const s = String(stored || '').toUpperCase();
  if (exact) return s === base;
  if (suffix) return s === `${base}-${suffix}` || s === `${base}${suffix}`;
  if (!s.startsWith(base)) return false;
  const rest = s.slice(base.length);
  return rest === '' || /^-[A-Z0-9]{1,4}$/.test(rest) || /^[A-Z]{1,3}$/.test(rest);
};

/**
 * What is this query likely to be? Returns the sources worth asking, most
 * specific first. Anything unrecognised falls through to places + a broad
 * tenement-holder search, which is what a bare word usually means.
 */
export function classify(raw) {
  const input = String(raw || '').trim();
  if (!input) return [];

  if (parseTenId(input)) return ['tenement'];

  // A-Number: "A12345" or "a 12345".
  if (/^a\s*\d{2,7}$/i.test(input)) return ['anumberSoil', 'anumberDrill'];

  // MINEDEX site code: "S0001234" or "s1234". Abandoned mine features share
  // the same id space but are a DISJOINT set of records - verified, 0 of 250
  // sampled codes exist in both - so an S-number has to be asked of both or it
  // will miss whichever half it is not in.
  if (/^s\s*\d{3,9}$/i.test(input)) return ['minedexSite', 'abandonedMine'];

  // Bare digits could be an A-Number or a sample/hole ID - ask all of them.
  if (/^\d{2,9}$/.test(input)) return ['anumberSoil', 'anumberDrill', 'minedexSite', 'abandonedMine', 'soilSample', 'drillHole'];

  // Anything with a letter and a digit is most likely a company sample or hole
  // ID (e.g. "ABC-001", "KLG12"). Places are still asked, cheaply, in parallel.
  if (/[a-z]/i.test(input) && /\d/.test(input)) return ['soilSample', 'drillHole', 'minedexSite', 'place'];

  return ['place', 'minedexSite', 'tenementHolder'];
}

const digitsOf = (s) => String(s).replace(/\D/g, '');

const SOURCES = {
  place: {
    label: 'Places',
    async run(input, { signal, apiKey }) {
      if (!apiKey) return [];
      const url = `https://api.maptiler.com/geocoding/${encodeURIComponent(input)}.json`;
      const { data } = await axios.get(url, {
        signal,
        params: { key: apiKey, bbox: WA_BBOX.join(','), limit: 8 },
      });
      return (data.features || []).map((f) => ({
        id: `place:${f.id}`,
        source: 'place',
        title: f.text || f.place_name,
        subtitle: f.place_name,
        meta: (f.place_type || [])[0],
        lngLat: { lng: f.center[0], lat: f.center[1] },
        bbox: f.bbox || null,
        feature: f,
      }));
    },
  },

  tenement: {
    label: 'Tenements',
    async run(input, { signal }) {
      const parsed = parseTenId(input);
      if (!parsed) return [];
      const { base, suffix, exact } = parsed;

      // A typed suffix is matched whole, against both the hyphenated and the
      // bare storage forms. A bare base is matched by prefix so it still finds
      // its suffixed variants, then narrowed by tenIdMatches - the LIKE alone
      // would also return "E 04/11310" for "E 04/1131".
      //
      // fmt_tenid has no index (the extractor builds geom, tenstatus, type,
      // the three dates and holdername - not this), so both forms are a ~0.6s
      // sequential scan either way. Prefix vs equality costs nothing extra.
      let cql;
      if (exact) cql = `fmt_tenid = ${q(base)}`;
      else if (suffix) cql = `fmt_tenid = ${q(`${base}-${suffix}`)} OR fmt_tenid = ${q(`${base}${suffix}`)}`;
      else cql = `fmt_tenid LIKE ${q(`${base}%`)}`;

      const { data } = await wfs('ne:wa_tenements', cql, { limit: 50, signal });
      return (data.features || [])
        .filter((f) => tenIdMatches(f.properties.fmt_tenid, parsed))
        .map((f) => ({
          id: `ten:${f.id}`,
          source: 'tenement',
          title: f.properties.fmt_tenid,
          subtitle: f.properties.holdername || 'No holder recorded',
          meta: [f.properties.tenstatus, f.properties.type].filter(Boolean).join(' · '),
          lngLat: centreOf(f.geometry),
          bbox: bboxOf(f.geometry),
          feature: f,
        }));
    },
  },

  tenementHolder: {
    label: 'Tenement holders',
    async run(input, { signal }) {
      const { data } = await wfs(
        'ne:wa_tenements',
        `holdername ILIKE ${q(`%${input}%`)}`,
        { limit: 100, signal }
      );
      return (data.features || []).map((f) => ({
        id: `ten:${f.id}`,
        source: 'tenement',
        title: f.properties.fmt_tenid,
        subtitle: f.properties.holdername,
        meta: [f.properties.tenstatus, f.properties.type].filter(Boolean).join(' · '),
        lngLat: centreOf(f.geometry),
        bbox: bboxOf(f.geometry),
        feature: f,
      }));
    },
  },

  anumberSoil: {
    label: 'Soil samples in A-Number',
    async run(input, { signal }) {
      const n = digitsOf(input);
      if (!n) return [];
      const { data } = await wfs('ne:wa_au_soil_assays', `anumber = ${n}`, { limit: 2000, signal });
      const features = data.features || [];
      if (!features.length) return [];
      // One row for the whole report, not one per sample - an A-Number can hold
      // thousands, and what you want is to be taken to the survey.
      return [aggregateRow(features, {
        id: `anum-soil:${n}`,
        source: 'anumberSoil',
        title: `A${n}`,
        subtitle: `${features.length} soil sample${features.length === 1 ? '' : 's'}`,
        meta: 'Soil A-Number',
      })];
    },
  },

  anumberDrill: {
    label: 'Drill holes in A-Number',
    async run(input, { signal }) {
      const n = digitsOf(input);
      if (!n) return [];
      const { data } = await wfs('ne:wa_au_drill_holes', `anumber = ${n}`, { limit: 2000, signal });
      const features = data.features || [];
      if (!features.length) return [];
      return [aggregateRow(features, {
        id: `anum-dh:${n}`,
        source: 'anumberDrill',
        title: `A${n}`,
        subtitle: `${features.length} drill-hole interval${features.length === 1 ? '' : 's'}`,
        meta: 'Drill-hole A-Number',
      })];
    },
  },

  soilSample: {
    label: 'Soil samples',
    // Whole of WA - see SLOW_WITHOUT_INDEX.
    needsIndex: true,
    async run(input, { signal }) {
      const bbox = WA_BBOX;
      const numeric = /^\d+$/.test(input);
      const cql = numeric
        ? `id = ${input} OR surfacesampleid = ${input} OR companysampleid = ${q(input)}`
        : `companysampleid ILIKE ${q(`%${input}%`)}`;
      const { data } = await wfs('ne:wa_au_soil_assays', cql, { limit: 100, signal, bbox });
      return (data.features || []).map((f) => ({
        id: `soil:${f.id}`,
        source: 'soilSample',
        title: f.properties.companysampleid || `Sample ${f.properties.surfacesampleid}`,
        subtitle: `A${f.properties.anumber} · ${f.properties.surfacesampletype || 'unknown type'}`,
        meta: f.properties.au_ppb != null ? `${f.properties.au_ppb} ppb Au` : 'No assay',
        lngLat: centreOf(f.geometry),
        feature: f,
      }));
    },
  },

  minedexSite: {
    label: 'MINEDEX sites',
    async run(input, { signal }) {
      // site_code is the S-number on a MINEDEX record; site_title is the mine
      // or prospect name, which is what people actually remember.
      //
      // Codes are stored zero-padded to "S" + 7 digits, so a bare "26970" has
      // to be padded back to S0026970 before it will match anything. Both the
      // padded form and whatever was typed are tried, so "S0026970",
      // "s0026970" and "26970" all land on the same site.
      const isCode = /^s?\s*\d{3,9}$/i.test(input);
      const digits = input.replace(/\D/g, '');
      const padded = `S${digits.padStart(7, '0')}`;
      const cql = isCode
        ? `site_code = ${q(padded)} OR site_code = ${q(input.toUpperCase())}`
        : `site_title ILIKE ${q(`%${input}%`)} OR short_name ILIKE ${q(`%${input}%`)}`;
      const { data } = await wfs('ne:wa_minedex_sites', cql, { limit: 60, signal });
      return (data.features || []).map((f) => ({
        id: `mdx:${f.id}`,
        source: 'minedexSite',
        title: f.properties.short_name || f.properties.site_title || f.properties.site_code,
        subtitle: [f.properties.site_title, f.properties.site_stage].filter(Boolean).join(' · '),
        meta: f.properties.commodity || f.properties.site_commo,
        lngLat: centreOf(f.geometry),
        feature: f,
      }));
    },
  },

  abandonedMine: {
    label: 'Abandoned mine features',
    async run(input, { signal }) {
      // Same S-number padding as minedexSite, because it is the same id space -
      // but a DIFFERENT set of records. wabmines_no is the inventory's own
      // reference (e.g. AMB0001) and is what appears on some old reports.
      const isCode = /^s?\s*\d{3,9}$/i.test(input);
      const digits = input.replace(/\D/g, '');
      const padded = `S${digits.padStart(7, '0')}`;
      const cql = isCode
        ? `site_code = ${q(padded)} OR site_code = ${q(input.toUpperCase())}`
        : `wabmines_no ILIKE ${q(`%${input}%`)}`;
      const { data } = await wfs('ne:wa_abandoned_mines', cql, { limit: 60, signal });
      return (data.features || []).map((f) => ({
        id: `abm:${f.id}`,
        source: 'abandonedMine',
        // feature_type is the useful label but is populated on only 35% of
        // rows, so feature_group carries the rest rather than showing a blank.
        title: f.properties.feature_type || f.properties.feature_group || f.properties.site_code,
        subtitle: [f.properties.site_code, f.properties.target_commodity].filter(Boolean).join(' · '),
        meta: f.properties.condition ? `condition ${f.properties.condition}` : null,
        lngLat: centreOf(f.geometry),
        feature: f,
      }));
    },
  },

  drillHole: {
    label: 'Drill holes',
    // Whole of WA - see SLOW_WITHOUT_INDEX.
    needsIndex: true,
    async run(input, { signal }) {
      const bbox = WA_BBOX;
      const numeric = /^\d+$/.test(input);
      const cql = numeric
        ? `dhassayid = ${input} OR collar_id = ${input} OR companyholeid = ${q(input)}`
        : `companyholeid ILIKE ${q(`%${input}%`)}`;
      const { data } = await wfs('ne:wa_au_drill_holes', cql, { limit: 100, signal, bbox });
      return (data.features || []).map((f) => ({
        id: `dh:${f.id}`,
        source: 'drillHole',
        title: f.properties.companyholeid || `Hole ${f.properties.holeid}`,
        subtitle: `A${f.properties.anumber} · ${f.properties.holetype || 'unknown type'}`
          + (f.properties.fromdepth != null ? ` · ${f.properties.fromdepth}-${f.properties.todepth}m` : ''),
        meta: f.properties.au_gpt != null ? `${Number(f.properties.au_gpt).toFixed(2)} g/t Au` : 'No assay',
        lngLat: centreOf(f.geometry),
        feature: f,
      }));
    },
  },
};

/** Collapses a whole A-Number's features into one row covering their extent. */
function aggregateRow(features, base) {
  const lngs = [];
  const lats = [];
  features.forEach((f) => {
    const c = centreOf(f.geometry);
    if (c) { lngs.push(c.lng); lats.push(c.lat); }
  });
  const bbox = lngs.length
    ? [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)]
    : null;
  return {
    ...base,
    lngLat: lngs.length
      ? { lng: (Math.min(...lngs) + Math.max(...lngs)) / 2, lat: (Math.min(...lats) + Math.max(...lats)) / 2 }
      : null,
    bbox,
    features,
  };
}

export const SOURCE_LABELS = Object.fromEntries(
  Object.entries(SOURCES).map(([k, v]) => [k, v.label])
);

/**
 * Runs every source `classify` picked, in parallel, and returns the rows that
 * came back. A source that fails is dropped rather than failing the search -
 * one flaky WFS call should not hide the results that did arrive.
 */
export async function runSearch(input, { signal, apiKey } = {}) {
  const wanted = classify(input);

  // Sources that need an index we have not built yet are held back rather than
  // allowed to hang the map on a sequential scan. The caller is told, so a
  // fruitless search can say why instead of just "no match".
  const skipped = SLOW_WITHOUT_INDEX ? wanted.filter((n) => SOURCES[n].needsIndex) : [];
  const sources = wanted.filter((n) => !skipped.includes(n));
  if (!sources.length) return { results: [], sources, skipped };

  const settled = await Promise.allSettled(
    sources.map((name) => SOURCES[name].run(input.trim(), { signal, apiKey }))
  );

  const results = [];
  settled.forEach((r, i) => {
    if (r.status === 'fulfilled') results.push(...r.value);
    else if (r.reason?.name !== 'CanceledError' && r.reason?.name !== 'AbortError') {
      console.warn(`Search source "${sources[i]}" failed:`, r.reason?.message || r.reason);
    }
  });

  // Rank by the order `classify` returned, which is already "most likely first"
  // for THIS query - a plain word means the place, an ID means the dataset.
  //
  // This used to be a fixed table that always sank places to the bottom. For
  // "Kalgoorlie" that put the town at row 101, under a hundred tenements whose
  // holder happens to contain the word, which reads as geocoding being broken.
  const rank = Object.fromEntries(sources.map((name, i) => [name, i]));
  results.sort((a, b) => (rank[a.source] ?? 99) - (rank[b.source] ?? 99));
  return { results, sources, skipped };
}
